import { useEffect, useRef, useState, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import * as THREE from 'three';
import axios from 'axios';

/* ─── Colour tokens ─────────────────────────────────────────────────── */
const C = {
  navyDeep: '#0F1C2E',
  navy: '#1A2E4A',
  gold: '#F2785C',
  coral: '#F2785C',
  cream: '#F7EBD9',
  creamLight: '#FDF5EC',
  teal: '#4EBFBF',
  reggaeGreen: '#2D7D4F',
  white: '#FFFFFF',
};

const WHATSAPP_URL = 'https://wa.me/+17874222734';
const PLAN_PDF_URL = '/clients/cuando-sale-el-sol/Boris_Bilbraut.pdf';
const PLAN_PDF_FILENAME = 'Boris_Bilbraut_Cuando_Sale_el_Sol_Campaign_Plan.pdf';

const responsiveCss = `
  .csel-page * { box-sizing: border-box; }
  .csel-page a, .csel-page button { -webkit-tap-highlight-color: transparent; }
  .csel-container { max-width: 1280px; margin: 0 auto; padding: 0 48px; }
  .csel-section { padding: 120px 0; }
  .csel-hero { min-height: 100svh; }
  .csel-hero-content { position: relative; z-index: 5; padding: 0 48px; max-width: 1280px; margin: 0 auto; width: 100%; }
  .csel-hero-title { letter-spacing: 0 !important; }
  .csel-hero-copy { font-size: 18px; }
  .csel-hero-logo { position: absolute; top: 40px; left: 48px; z-index: 10; }
  .csel-hero-badge { position: absolute; top: 40px; right: 48px; z-index: 10; }
  .csel-hero-portrait { position: absolute; right: 0; top: 0; bottom: 0; width: 50%; z-index: 2; overflow: hidden; }
  .csel-stats-strip { position: relative; z-index: 5; margin-top: 64px; border-top: 1px solid rgba(255,255,255,0.08); display: flex; background: rgba(15,28,46,0.7); backdrop-filter: blur(16px); overflow-x: auto; }
  .csel-stat-chip { min-width: 168px; }
  .csel-grid-artist { display: grid; grid-template-columns: 1fr 1.1fr; gap: 80px; align-items: center; }
  .csel-grid-two { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: start; }
  .csel-grid-opportunity { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
  .csel-snapshot-grid { display: grid; grid-template-columns: repeat(3, 1fr); border-top: 2px solid ${C.gold}; border-bottom: 2px solid rgba(242,120,92,0.25); }
  .csel-agency-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .csel-kpi-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; margin-bottom: 48px; }
  .csel-scale-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .csel-next-grid { display: grid; grid-template-columns: 1fr 1.4fr; gap: 100px; align-items: start; }
  .csel-week-row { display: grid; grid-template-columns: 72px 180px 110px 1fr; gap: 24px; }
  .csel-step-row { display: grid; grid-template-columns: 40px 80px 1fr 100px; gap: 20px; }
  .csel-cta-row { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; }
  .csel-button { min-height: 52px; display: inline-flex !important; align-items: center; justify-content: center; text-align: center; }
  .csel-final-cta { padding: 56px 48px; background: ${C.navyDeep}; border-top: 1px solid rgba(255,255,255,0.07); display: flex; align-items: center; justify-content: space-between; gap: 28px; }
  .csel-final-copy { max-width: 560px; }

  @media (max-width: 1024px) {
    .csel-container { padding: 0 32px; }
    .csel-section { padding: 88px 0; }
    .csel-grid-artist,
    .csel-grid-two,
    .csel-next-grid { grid-template-columns: 1fr !important; gap: 56px !important; }
    .csel-grid-opportunity,
    .csel-scale-grid { grid-template-columns: 1fr !important; }
    .csel-snapshot-grid,
    .csel-agency-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .csel-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; }
  }

  @media (max-width: 700px) {
    .csel-section { padding: 68px 0; }
    .csel-container { padding: 0 20px; }
    .csel-hero { min-height: auto !important; padding-top: 112px; }
    .csel-hero-content { padding: 0 20px !important; }
    .csel-hero-logo { top: 22px !important; left: 20px !important; }
    .csel-hero-logo img { height: 30px !important; }
    .csel-hero-badge { top: 64px !important; left: 20px !important; right: auto !important; max-width: calc(100vw - 40px); padding: 7px 12px !important; font-size: 9px !important; letter-spacing: 0.16em !important; }
    .csel-hero-portrait { width: 100% !important; height: 56%; bottom: auto !important; opacity: 0.55; }
    .csel-hero-portrait img { object-position: center top !important; }
    .csel-hero-title { font-size: clamp(54px, 18vw, 78px) !important; line-height: 0.92 !important; }
    .csel-hero-copy { font-size: 15px !important; line-height: 1.6 !important; max-width: 92vw !important; margin-top: 20px !important; }
    .csel-stats-strip { margin-top: 44px !important; display: grid !important; grid-template-columns: repeat(2, minmax(0, 1fr)); overflow: visible; }
    .csel-stat-chip { min-width: 0; padding: 18px 12px !important; border-left: none !important; border-top: 1px solid rgba(242,120,92,0.16); }
    .csel-stat-chip span:first-child { font-size: 30px !important; }
    .csel-snapshot-grid,
    .csel-agency-grid,
    .csel-kpi-grid { grid-template-columns: 1fr !important; }
    .csel-snapshot-item { border-right: none !important; border-bottom: 1px solid rgba(255,255,255,0.07) !important; padding: 30px 0 !important; }
    .csel-hook-card { flex-basis: 82vw !important; padding: 34px 26px !important; }
    .csel-week-row { grid-template-columns: 1fr auto !important; gap: 8px 16px !important; padding: 20px 0 !important; }
    .csel-week-dates, .csel-week-activities { grid-column: 1 / -1; }
    .csel-week-spend { text-align: right; }
    .csel-step-row { grid-template-columns: 36px 1fr auto !important; gap: 12px !important; }
    .csel-step-when { grid-column: 2 / 3; }
    .csel-step-task { grid-column: 1 / -1; padding-top: 0 !important; }
    .csel-step-cost { grid-column: 3 / 4; grid-row: 1 / 3; align-self: start; min-width: 72px; }
    .csel-meta-row { grid-template-columns: 1fr !important; gap: 6px !important; }
    .csel-player-bar { align-items: flex-start !important; flex-direction: column; gap: 18px; }
    .csel-cta-row { flex-direction: column; align-items: stretch; }
    .csel-button { width: 100%; padding-left: 18px !important; padding-right: 18px !important; }
    .csel-final-cta { padding: 44px 20px !important; flex-direction: column; align-items: stretch; }
    .csel-final-copy { max-width: none; }
    .csel-footer { padding: 28px 20px !important; justify-content: center !important; text-align: center; }
  }

  @media (prefers-reduced-motion: reduce) {
    .csel-page *, .csel-page *::before, .csel-page *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      scroll-behavior: auto !important;
      transition-duration: 0.001ms !important;
    }
    .csel-page canvas { display: none; }
  }
`;

/* ─── Inline style helpers ──────────────────────────────────────────── */
const css = {
  page: {
    fontFamily: "'Source Sans Pro', Arial, sans-serif",
    background: C.navyDeep,
    color: C.cream,
    overflowX: 'hidden',
    scrollBehavior: 'smooth',
  },
  eyebrow: {
    fontFamily: "'Poppins', Verdana, sans-serif",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.28em',
    textTransform: 'uppercase',
    color: C.coral,
  },
  display: {
    fontFamily: "'Poppins', Verdana, sans-serif",
    fontWeight: 900,
  },
  label: {
    fontFamily: "'Poppins', Verdana, sans-serif",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: C.teal,
  },
};

/* ─── Intersection Observer hook ───────────────────────────────────── */
function useReveal(threshold = 0.18) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ─── Diamond shape ─────────────────────────────────────────────────── */
function Diamond({ size = 20, color = C.coral, style = {} }) {
  return (
    <span style={{
      display: 'inline-block',
      width: size,
      height: size,
      background: color,
      transform: 'rotate(45deg)',
      flexShrink: 0,
      ...style,
    }} />
  );
}

/* ─── Three.js particle hero background ────────────────────────────── */
function SunriseParticles({ canvasRef }) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false });
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, canvas.clientWidth / canvas.clientHeight, 0.1, 1000);
    camera.position.z = 80;

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);

    const COUNT = 1200;
    const positions = new Float32Array(COUNT * 3);
    const colors = new Float32Array(COUNT * 3);
    const speeds = new Float32Array(COUNT);

    const palette = [
      new THREE.Color(C.gold),
      new THREE.Color(C.coral),
      new THREE.Color(C.teal),
      new THREE.Color('#F7C59F'),
    ];

    for (let i = 0; i < COUNT; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 180;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 100;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 80;
      const c = palette[Math.floor(Math.random() * palette.length)];
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
      speeds[i] = 0.004 + Math.random() * 0.012;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const mat = new THREE.PointsMaterial({
      size: 0.65,
      vertexColors: true,
      transparent: true,
      opacity: 0.7,
      sizeAttenuation: true,
    });

    const points = new THREE.Points(geo, mat);
    scene.add(points);

    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const pos = geo.attributes.position.array;
      for (let i = 0; i < COUNT; i++) {
        pos[i * 3 + 1] += speeds[i];
        if (pos[i * 3 + 1] > 52) pos[i * 3 + 1] = -52;
      }
      geo.attributes.position.needsUpdate = true;
      points.rotation.y += 0.00015;
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, [canvasRef]);
  return null;
}

/* ─── Stat chip ─────────────────────────────────────────────────────── */
function StatChip({ label, value }) {
  return (
    <div className="csel-stat-chip" style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '20px 32px',
      borderLeft: `2px solid rgba(242,120,92,0.25)`,
      gap: 4,
    }}>
      <span style={{ ...css.display, fontSize: 36, color: C.gold, lineHeight: 1 }}>{value}</span>
      <span style={{ ...css.label, color: 'rgba(247,235,217,0.55)' }}>{label}</span>
    </div>
  );
}

/* ─── Reveal wrapper ────────────────────────────────────────────────── */
function Reveal({ children, delay = 0, style = {} }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(32px)',
      transition: `opacity 0.75s ease ${delay}s, transform 0.75s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

/* ─── Section wrapper ───────────────────────────────────────────────── */
function Section({ children, bg = C.navyDeep, style = {}, id }) {
  return (
    <section id={id} className="csel-section" style={{
      background: bg,
      position: 'relative',
      overflow: 'hidden',
      ...style,
    }}>
      <div className="csel-container">
        {children}
      </div>
    </section>
  );
}

/* ─── Hook card ─────────────────────────────────────────────────────── */
function HookCard({ num, title, desc, accent }) {
  return (
    <div className="csel-hook-card" style={{
      flex: '0 0 380px',
      background: 'rgba(255,255,255,0.04)',
      border: `1px solid rgba(255,255,255,0.1)`,
      backdropFilter: 'blur(12px)',
      padding: '44px 40px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 4,
        background: accent,
      }} />
      <div style={{
        ...css.display, fontSize: 80, fontWeight: 700,
        color: accent, opacity: 0.18, lineHeight: 1,
        position: 'absolute', top: 16, right: 24,
      }}>
        {num}
      </div>
      <div style={{ ...css.eyebrow, color: accent, marginBottom: 16 }}>Hook #{num}</div>
      <h3 style={{
        ...css.display, fontSize: 30, color: C.cream,
        margin: '0 0 20px', lineHeight: 1.25, maxWidth: 280,
      }}>
        {title}
      </h3>
      <p style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(247,235,217,0.7)', margin: 0 }}>
        {desc}
      </p>
    </div>
  );
}

/* ─── KPI card ──────────────────────────────────────────────────────── */
function KpiCard({ metric, target, platform, accent = C.teal }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{
      background: 'rgba(255,255,255,0.04)',
      border: `1px solid rgba(255,255,255,0.08)`,
      padding: '36px 32px',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      transition: 'opacity 0.6s ease, transform 0.6s ease',
    }}>
      <div style={{ ...css.label, color: accent, marginBottom: 12 }}>{platform}</div>
      <div style={{ ...css.display, fontSize: 42, color: C.gold, lineHeight: 1, marginBottom: 12 }}>{target}</div>
      <div style={{ fontSize: 14, color: 'rgba(247,235,217,0.7)', lineHeight: 1.5 }}>{metric}</div>
    </div>
  );
}

/* ─── Budget row ─────────────────────────────────────────────────────── */
function WeekRow({ week, dates, spend, activities, isLast }) {
  const isPeak = week === 'W5';
  return (
    <div className="csel-week-row" style={{
      gap: 24,
      padding: '18px 0',
      borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.07)',
      alignItems: 'start',
      background: isPeak ? 'rgba(242,120,92,0.04)' : 'transparent',
    }}>
      <span style={{ ...css.display, fontSize: 22, color: isPeak ? C.gold : C.coral, fontWeight: 700 }}>{week}</span>
      <span className="csel-week-dates" style={{ fontSize: 13, color: 'rgba(247,235,217,0.5)', paddingTop: 3 }}>{dates}</span>
      <span className="csel-week-spend" style={{ ...css.display, fontSize: 20, color: C.teal, fontWeight: 600 }}>{spend}</span>
      <span className="csel-week-activities" style={{ fontSize: 14, color: 'rgba(247,235,217,0.75)', lineHeight: 1.55 }}>{activities}</span>
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────────────── */
export default function CuandoSaleElSol() {
  const canvasRef = useRef(null);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start', dragFree: true });
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false);

  const downloadPlan = useCallback(async (event) => {
    event.preventDefault();

    try {
      const response = await fetch(PLAN_PDF_URL);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = objectUrl;
      link.download = PLAN_PDF_FILENAME;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(PLAN_PDF_URL, '_blank', 'noopener,noreferrer');
    }
  }, []);

  /* noindex + page view notification */
  useEffect(() => {
    /* noindex */
    let metaRobots = document.querySelector('meta[name="robots"]');
    const created = !metaRobots;
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    const prev = metaRobots.content;
    metaRobots.content = 'noindex,nofollow';

    /* title */
    const prevTitle = document.title;
    document.title = 'Cuando Sale el Sol — Campaign Deck · Mikel Media';

    /* fire page view */
    axios.post('/api/page-view', {
      page: 'cuando-sale-el-sol',
      referrer: document.referrer,
      user_agent: navigator.userAgent,
    }).catch(() => {});

    return () => {
      if (created) document.head.removeChild(metaRobots);
      else metaRobots.content = prev;
      document.title = prevTitle;
    };
  }, []);

  /* Embla carousel callbacks */
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  /* ─── Render ───────────────────────────────────────────────────────── */
  return (
    <div className="csel-page" style={css.page}>
      <style>{responsiveCss}</style>

      {/* ── HERO ────────────────────────────────────────────────────── */}
      <section className="csel-hero" style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        overflow: 'hidden',
      }}>
        {/* gradient bg */}
        <div style={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(ellipse 120% 80% at 60% 110%, #6B3220 0%, #2A1A0E 40%, ${C.navyDeep} 75%)`,
          zIndex: 0,
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 80% 60% at 50% 90%, rgba(242,120,92,0.18) 0%, transparent 65%)',
          zIndex: 0,
        }} />

        {/* Three.js canvas */}
        <canvas ref={canvasRef} style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1,
        }} />
        <SunriseParticles canvasRef={canvasRef} />

        {/* boris portrait fade */}
        <div className="csel-hero-portrait" style={{
          position: 'absolute', right: 0, top: 0, bottom: 0, width: '50%',
          zIndex: 2, overflow: 'hidden',
        }}>
          <img
            src="/clients/cuando-sale-el-sol/boris-portrait.png"
            alt="Boris Bilbraut"
            style={{
              width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top',
              mixBlendMode: 'luminosity', opacity: 0.3,
            }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(90deg, ${C.navyDeep} 0%, transparent 50%, transparent 100%)`,
          }} />
        </div>

        {/* logo */}
        <div className="csel-hero-logo" style={{ position: 'absolute', top: 40, left: 48, zIndex: 10 }}>
          <img src="/clients/cuando-sale-el-sol/logo-mikel-white.png" alt="Mikel Media" style={{ height: 40, opacity: 0.9 }} />
        </div>

        {/* badge */}
        <div className="csel-hero-badge" style={{
          position: 'absolute', top: 40, right: 48, zIndex: 10,
          border: `1px solid rgba(242,120,92,0.35)`,
          padding: '8px 18px',
          ...css.label, color: 'rgba(247,235,217,0.6)',
        }}>
          Viral Campaign Proposal · Summer 2026
        </div>

        {/* hero content */}
        <div className="csel-hero-content" style={{ position: 'relative', zIndex: 5, padding: '0 48px 0', maxWidth: 1280, margin: '0 auto', width: '100%' }}>
          <div style={{ ...css.eyebrow, marginBottom: 24, color: C.coral }}>
            Boris Bilbraut y los Árboles
          </div>
          <h1 className="csel-hero-title" style={{
            ...css.display, fontSize: 'clamp(80px, 12vw, 160px)',
            margin: 0, lineHeight: 0.88, letterSpacing: 0,
            color: C.cream,
          }}>
            Cuando<br />
            <span style={{ color: C.gold }}>Sale el Sol</span>
          </h1>
          <p className="csel-hero-copy" style={{
            fontSize: 18, color: 'rgba(247,235,217,0.65)', marginTop: 28, maxWidth: 560,
            lineHeight: 1.65, letterSpacing: '0.01em',
          }}>
            Caribbean reggae · New beginnings · The summer song of 2026 for the Spanish-speaking world.
          </p>
        </div>

        {/* stats strip */}
        <div className="csel-stats-strip" style={{
          position: 'relative', zIndex: 5,
          marginTop: 64,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          background: 'rgba(15,28,46,0.7)',
          backdropFilter: 'blur(16px)',
        }}>
          <StatChip label="Budget" value="$9,000" />
          <StatChip label="Timeline" value="8 Weeks" />
          <StatChip label="Markets" value="Pan-LATAM" />
          <StatChip label="Target Reach" value="8M+" />
          <StatChip label="Creator Videos" value="150+" />
          <StatChip label="New Streams" value="150K" />
        </div>
      </section>


      {/* ── THE ARTIST ──────────────────────────────────────────────── */}
      <Section id="artist" bg={C.creamLight} style={{ color: C.navyDeep }}>
        <div className="csel-grid-artist" style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 80, alignItems: 'center' }}>
          {/* portrait */}
          <Reveal>
            <div style={{ position: 'relative' }}>
              <div style={{
                width: '100%', paddingBottom: '120%', position: 'relative', overflow: 'hidden',
                clipPath: 'polygon(12% 0%, 88% 0%, 100% 12%, 100% 88%, 88% 100%, 12% 100%, 0% 88%, 0% 12%)',
              }}>
                <img
                  src="/clients/cuando-sale-el-sol/boris-portrait.png"
                  alt="Boris Bilbraut"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
                />
              </div>
              <Diamond size={32} color={C.coral} style={{ position: 'absolute', top: -16, right: -16 }} />
              <Diamond size={20} color={C.teal} style={{ position: 'absolute', bottom: 24, left: -12 }} />
            </div>
          </Reveal>

          {/* bio */}
          <Reveal delay={0.15}>
            <div style={{ ...css.eyebrow, color: C.coral, marginBottom: 20 }}>The Artist</div>
            <h2 style={{
              ...css.display, fontSize: 'clamp(42px, 5vw, 72px)',
              color: C.navyDeep, margin: '0 0 28px', lineHeight: 1.05, letterSpacing: 0,
            }}>
              Boris Bilbraut<br />y los Árboles
            </h2>
            <p style={{ fontSize: 17, lineHeight: 1.75, color: '#1A2E4A', marginBottom: 18 }}>
              Founding drummer of <strong style={{ color: C.coral }}>Cultura Profética</strong> — his organic,
              precise, deeply emotional approach helped define Latin reggae and shaped a sound that crossed genres,
              generations, and geographies.
            </p>
            <p style={{ fontSize: 17, lineHeight: 1.75, color: '#1A2E4A' }}>
              Today he enters a new phase — the legacy is not the destination, it is the starting point.
            </p>

            {/* stats */}
            <div style={{ display: 'flex', gap: 0, marginTop: 40, borderTop: `2px solid ${C.navyDeep}`, paddingTop: 32 }}>
              {[
                { n: '19,278', l: 'Monthly Listeners' },
                { n: '559', l: 'Spotify Followers' },
                { n: 'PR · CO · MX', l: 'Top Markets' },
              ].map((s, i) => (
                <div key={i} style={{
                  flex: 1,
                  paddingLeft: i === 0 ? 0 : 24,
                  borderLeft: i === 0 ? 'none' : `2px solid rgba(242,120,92,0.3)`,
                  marginLeft: i === 0 ? 0 : 24,
                }}>
                  <div style={{ ...css.display, fontSize: 28, color: C.navyDeep, fontWeight: 700 }}>{s.n}</div>
                  <div style={{ ...css.label, color: C.coral, marginTop: 4 }}>{s.l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </Section>


      {/* ── THE SONG ────────────────────────────────────────────────── */}
      <Section id="song" bg="#111827">
        <div className="csel-grid-two" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'start' }}>
          {/* meta */}
          <Reveal>
            <div style={{ ...css.eyebrow, marginBottom: 20 }}>The Song</div>
            <h2 style={{
              ...css.display, fontSize: 'clamp(48px, 6vw, 88px)',
              color: C.cream, margin: '0 0 24px', letterSpacing: 0, lineHeight: 0.95,
            }}>
              Cuando<br />
              <span style={{ color: C.gold }}>Sale el Sol</span>
            </h2>
            <p style={{ fontSize: 16, lineHeight: 1.75, color: 'rgba(247,235,217,0.7)', marginBottom: 36 }}>
              Caribbean reggae, warm and uplifting. A song about new beginnings — the morning after a hard night,
              the summer after a hard year.
            </p>

            {/* meta table */}
            <div>
              {[
                { k: 'Genre', v: 'Caribbean Reggae · Tropical Latin' },
                { k: 'Theme', v: 'New beginnings · Sunrise energy · Summer rebirth' },
                { k: 'Language', v: 'Spanish' },
                { k: 'Duration', v: '4:44 full  ·  0:00–0:15 viral clip' },
                { k: 'Release', v: 'April 10, 2026 · live on all major platforms' },
                { k: 'Traction', v: 'Existing airplay in Puerto Rico' },
              ].map((row, i) => (
                <div key={i} className="csel-meta-row" style={{
                  display: 'grid', gridTemplateColumns: '120px 1fr', gap: 24,
                  padding: '14px 0',
                  borderBottom: i < 5 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                }}>
                  <span style={{ ...css.label, color: C.coral }}>{row.k}</span>
                  <span style={{ fontSize: 15, color: 'rgba(247,235,217,0.8)' }}>{row.v}</span>
                </div>
              ))}
            </div>
          </Reveal>

          {/* spotify embed */}
          <Reveal delay={0.15}>
            <div style={{
              background: 'linear-gradient(180deg, rgba(29,185,84,0.12) 0%, rgba(26,31,44,0.95) 100%)',
              padding: 12,
              position: 'relative',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 16,
              boxShadow: '0 28px 80px rgba(0,0,0,0.26)',
            }}>
              <div
                dangerouslySetInnerHTML={{
                  __html: '<iframe data-testid="embed-iframe" style="border-radius:12px" src="https://open.spotify.com/embed/track/0ajHI9uJzkB4KH7d2iPDuA?utm_source=generator" width="100%" height="352" frameBorder="0" allowfullscreen="" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy"></iframe>',
                }}
              />
            </div>

            <div style={{
              marginTop: 24, padding: '20px 24px',
              background: 'rgba(242,120,92,0.08)',
              borderLeft: `3px solid ${C.gold}`,
            }}>
              <p style={{ margin: 0, fontSize: 15, color: 'rgba(247,235,217,0.8)', lineHeight: 1.65, fontStyle: 'italic' }}>
                "Cuando Sale el Sol" functions as both a physical observation and an emotional metaphor — making it
                <strong style={{ color: C.gold, fontStyle: 'normal' }}> culturally portable across every LATAM and US Hispanic market simultaneously.</strong>
              </p>
            </div>
          </Reveal>
        </div>
      </Section>


      {/* ── THE OPPORTUNITY ─────────────────────────────────────────── */}
      <Section id="opportunity" bg={C.navyDeep}>
        <Reveal>
          <div style={{ ...css.eyebrow, marginBottom: 16 }}>Why This Can Go Viral in Summer 2026</div>
          <h2 style={{
            ...css.display, fontSize: 'clamp(36px, 4.5vw, 64px)',
            color: C.cream, margin: '0 0 56px', letterSpacing: 0,
          }}>
            Two openings nobody is exploiting.
          </h2>
        </Reveal>

        <div className="csel-grid-opportunity" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <Reveal delay={0.1}>
            <div style={{
              background: `linear-gradient(135deg, rgba(242,120,92,0.12) 0%, rgba(242,120,92,0.06) 100%)`,
              border: '1px solid rgba(242,120,92,0.25)',
              padding: '52px 48px',
              height: '100%',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
                <Diamond size={18} color={C.coral} />
                <span style={{ ...css.label, color: C.coral }}>The Emotional Hook</span>
              </div>
              <h3 style={{ ...css.display, fontSize: 32, color: C.cream, margin: '0 0 20px', lineHeight: 1.2 }}>
                A phrase that travels every market at once.
              </h3>
              <p style={{ fontSize: 16, lineHeight: 1.75, color: 'rgba(247,235,217,0.75)', margin: 0 }}>
                "Cuando Sale el Sol" — when the sun comes out. In Spanish this functions as both a physical observation
                and an emotional metaphor: the morning after a hard night, the summer after a hard year.
              </p>
              <p style={{ fontSize: 16, lineHeight: 1.75, color: 'rgba(247,235,217,0.75)', marginTop: 16 }}>
                That dual meaning is what makes this song{' '}
                <strong style={{ color: C.gold }}>culturally portable</strong> across every
                LATAM and US Hispanic market simultaneously.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div style={{
              background: `linear-gradient(135deg, rgba(78,191,191,0.12) 0%, rgba(45,125,79,0.08) 100%)`,
              border: '1px solid rgba(78,191,191,0.2)',
              padding: '52px 48px',
              height: '100%',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
                <Diamond size={18} color={C.teal} />
                <span style={{ ...css.label, color: C.teal }}>The Algorithmic Opening</span>
              </div>
              <h3 style={{ ...css.display, fontSize: 32, color: C.cream, margin: '0 0 20px', lineHeight: 1.2 }}>
                Reggae is underrepresented where reggaetón is saturated.
              </h3>
              <p style={{ fontSize: 16, lineHeight: 1.75, color: 'rgba(247,235,217,0.75)', margin: 0 }}>
                Caribbean reggae has deep cultural roots and a growing international audience, but it is significantly
                underrepresented in viral TikTok campaigns versus reggaetón and urbano.
              </p>
              <p style={{ fontSize: 16, lineHeight: 1.75, color: 'rgba(247,235,217,0.75)', marginTop: 16 }}>
                A well-orchestrated sound-seeding campaign in this genre, at the right moment of summer,{' '}
                <strong style={{ color: C.teal }}>can own the space precisely because the competition is lower.</strong>
              </p>
            </div>
          </Reveal>
        </div>
      </Section>


      {/* ── CAMPAIGN SNAPSHOT ───────────────────────────────────────── */}
      <Section id="snapshot" bg="#0A1220">
        <Reveal>
          <div style={{ ...css.eyebrow, marginBottom: 16 }}>Campaign Snapshot</div>
          <h2 style={{
            ...css.display, fontSize: 'clamp(32px, 4vw, 58px)',
            color: C.cream, margin: '0 0 64px', letterSpacing: 0, maxWidth: 900,
          }}>
            The summer 2026 song for the Spanish-speaking world.
          </h2>
        </Reveal>

        <div className="csel-snapshot-grid" style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          borderTop: `2px solid ${C.gold}`, borderBottom: `2px solid rgba(242,120,92,0.25)`,
        }}>
          {[
            { label: 'Total Budget', val: '$9,000', sub: 'Tier 2 · multi-platform' },
            { label: 'Timeline', val: '8 Weeks', sub: 'Jun 1 – Jul 26, 2026' },
            { label: 'Markets', val: '6', sub: 'PR · COL · MEX · ARG · US Hisp · ESP' },
            { label: 'Creator Videos', val: '150+', sub: 'Nano + micro creators' },
            { label: 'Combined Impressions', val: '8M+', sub: 'Cross-platform reach' },
            { label: 'New Spotify Streams', val: '150K', sub: '8-week target' },
          ].map((item, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="csel-snapshot-item" style={{
                padding: '48px 40px',
                borderRight: [2, 5].includes(i) ? 'none' : '1px solid rgba(255,255,255,0.07)',
                borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.07)' : 'none',
              }}>
                <div style={{ ...css.label, color: 'rgba(247,235,217,0.45)', marginBottom: 12 }}>{item.label}</div>
                <div style={{ ...css.display, fontSize: 54, color: C.gold, lineHeight: 1, marginBottom: 8 }}>{item.val}</div>
                <div style={{ fontSize: 13, color: 'rgba(247,235,217,0.5)' }}>{item.sub}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>


      {/* ── VIRAL STRATEGY ──────────────────────────────────────────── */}
      <Section id="strategy" bg={C.navyDeep}>
        <Reveal>
          <div style={{ ...css.eyebrow, marginBottom: 16 }}>Viral Strategy</div>
          <h2 style={{
            ...css.display, fontSize: 'clamp(32px, 4vw, 56px)',
            color: C.cream, margin: '0 0 12px', letterSpacing: 0,
          }}>
            Four hooks. One summer.
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(247,235,217,0.55)', marginBottom: 48, maxWidth: 560, lineHeight: 1.65 }}>
            Caribbean reggae + new beginnings = the perfect summer sunrise content moment.
            Each hook targets a different creator archetype and sharing mechanic.
          </p>
        </Reveal>

        {/* Embla carousel */}
        <div style={{ overflow: 'hidden' }} ref={emblaRef}>
          <div style={{ display: 'flex', gap: 20, userSelect: 'none' }}>
            <HookCard
              num="1"
              title="Transformation Before / After"
              desc="Split screen: a hard moment vs. life today. Highest DM-share rate. Drives Instagram Reels reach. The hook phrase lands perfectly on the 'after' moment."
              accent={C.coral}
            />
            <HookCard
              num="2"
              title="Sunrise Walk POV"
              desc="Golden-hour POV walk at sunrise synced to the opening bars. Zero skill barrier. Highest replication volume. Works in every city, every country."
              accent={C.gold}
            />
            <HookCard
              num="3"
              title="Couple / Waking POV"
              desc="Waking your partner to the song. Tag-a-friend mechanic built in. Multiplies organic reach without paid spend — personal, shareable, emotional."
              accent={C.teal}
            />
            <HookCard
              num="4"
              title='Stitch Challenge "¿Cuándo salió tu sol?"'
              desc="Boris posts the seed: '¿Y tú, cuándo salió tu sol?' Every Stitch re-surfaces his post in FYP. Exponential UGC engine with no incremental spend."
              accent={C.reggaeGreen}
            />
          </div>
        </div>

        {/* carousel controls */}
        <div style={{ display: 'flex', gap: 12, marginTop: 32 }}>
          {[{ fn: scrollPrev, dis: prevBtnDisabled, label: '←' }, { fn: scrollNext, dis: nextBtnDisabled, label: '→' }].map(({ fn, dis, label }) => (
            <button
              key={label}
              onClick={fn}
              disabled={dis}
              style={{
                width: 48, height: 48, background: 'transparent',
                border: `1px solid ${dis ? 'rgba(255,255,255,0.15)' : C.coral}`,
                color: dis ? 'rgba(255,255,255,0.3)' : C.coral,
                fontSize: 18, cursor: dis ? 'default' : 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {label}
            </button>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: 16, fontSize: 13, color: 'rgba(247,235,217,0.4)' }}>
            Drag to explore
          </div>
        </div>

        {/* platform pipeline */}
        <Reveal delay={0.1} style={{ marginTop: 64 }}>
          <div style={{
            padding: '32px 40px',
            background: 'rgba(78,191,191,0.06)',
            border: '1px solid rgba(78,191,191,0.18)',
            display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
          }}>
            <span style={{ ...css.label, color: C.teal }}>Platform Pipeline</span>
            {[
              { p: 'TikTok', desc: 'Discovers the song' },
              { p: 'Instagram Reels', desc: 'Converts viewers to fans' },
              { p: 'Spotify', desc: 'Generates revenue' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {i > 0 && <span style={{ color: C.teal, fontSize: 18 }}>→</span>}
                <div>
                  <span style={{ fontSize: 14, color: C.cream, fontWeight: 600 }}>{item.p}</span>
                  <span style={{ fontSize: 13, color: 'rgba(247,235,217,0.5)', marginLeft: 8 }}>{item.desc}</span>
                </div>
              </div>
            ))}
            <span style={{ fontSize: 13, color: 'rgba(247,235,217,0.45)', marginLeft: 'auto', fontStyle: 'italic' }}>
              All three must run simultaneously.
            </span>
          </div>
        </Reveal>
      </Section>


      {/* ── BUDGET DEPLOYMENT ───────────────────────────────────────── */}
      <Section id="budget" bg="#0D1520">
        <Reveal>
          <div style={{ ...css.eyebrow, marginBottom: 16 }}>Budget Deployment</div>
          <h2 style={{
            ...css.display, fontSize: 'clamp(32px, 4vw, 56px)',
            color: C.cream, margin: '0 0 12px', letterSpacing: 0,
          }}>
            8-Week Spend Plan
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(247,235,217,0.5)', marginBottom: 48, maxWidth: 560 }}>
            June 1 – July 26, 2026 · Total: $9,000 (Tier 2)
          </p>
        </Reveal>

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <WeekRow week="W1" dates="Jun 1–7" spend="$350" activities="Spotify editorial pitch · Groover 50 curators · SoundCampaign test · 5 Fiverr creators · Boris stitch seed video" />
          <WeekRow week="W2" dates="Jun 8–14" spend="$900" activities="20 Fiverr creators live · Iberogram contract signed · Boris 2× TikTok Lives · Playlist Push Spotify push" />
          <WeekRow week="W3" dates="Jun 15–21" spend="$1,700" activities="Iberogram Wave 1 (COL/MEX) · Playlist Push TikTok · 10 Fiverr (ARG/BRA) · Analyze hook performance" />
          <WeekRow week="W4" dates="Jun 22–28" spend="$1,500" activities="Iberogram Wave 2 · SoundCampaign run 2 · First Spark Ads test ($100) · Mid-campaign review" />
          <WeekRow week="W5" dates="Jun 29–Jul 5" spend="$1,800" activities="PEAK: Spark Ads $700 · Meta Reels $400 · Iberogram US Hispanic wave · Playlist Push bundle" />
          <WeekRow week="W6" dates="Jul 6–12" spend="$1,300" activities="Sustain Spark Ads winners · YouTube Shorts wave · Press releases · Radio follow-up with stream data" />
          <WeekRow week="W7" dates="Jul 13–19" spend="$900" activities="Expand to Spain + US market · Boris TikTok Lives ×2 · SoundCampaign run 3 · Build album pitch deck" />
          <WeekRow week="W8" dates="Jul 20–26" spend="$550" activities="Deploy $500 reserve to best ROI channel · Boris teases album / next single · Full campaign report" isLast />
        </div>

        {/* agency grid */}
        <Reveal delay={0.1} style={{ marginTop: 64 }}>
          <div style={{ ...css.label, color: 'rgba(247,235,217,0.45)', marginBottom: 24 }}>Recommended Agencies & Partners</div>
          <div className="csel-agency-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {[
              { agency: 'SoundCampaign', role: 'TikTok + Spotify creators', budget: '$242', note: '5,700+ creators, 14-day runs' },
              { agency: 'Playlist Push', role: 'TikTok + Spotify playlists', budget: '$450', note: '3,300 TikTok creators, 4,000 playlists' },
              { agency: 'Iberogram', role: 'LATAM nano/micro influencers', budget: '$2,845', note: 'COL/MEX focus, TikTok + Reels, UGC' },
              { agency: 'Groover', role: 'Spotify + radio pitching', budget: '$323', note: 'Latin curators, 7-day feedback' },
              { agency: 'Fiverr / Upwork', role: 'Direct creator hiring', budget: '$1,461', note: 'Brazil, Mexico, Colombia — $20–$150/video' },
              { agency: 'Spark Ads + Meta', role: 'Paid amplification', budget: '$2,300', note: 'TikTok Spark Ads + Instagram Reels' },
            ].map((a, i) => (
              <div key={i} style={{
                padding: '24px 28px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}>
                <div style={{ ...css.display, fontSize: 18, color: C.cream, fontWeight: 600, marginBottom: 6 }}>{a.agency}</div>
                <div style={{ fontSize: 13, color: 'rgba(247,235,217,0.55)', marginBottom: 12, lineHeight: 1.5 }}>{a.role}</div>
                <div style={{ ...css.display, fontSize: 24, color: C.teal, fontWeight: 700 }}>{a.budget}</div>
                <div style={{ fontSize: 11, color: 'rgba(247,235,217,0.35)', marginTop: 4 }}>{a.note}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </Section>


      {/* ── KPI FRAMEWORK ───────────────────────────────────────────── */}
      <Section id="kpis" bg={C.navyDeep}>
        <Reveal>
          <div style={{ ...css.eyebrow, marginBottom: 16 }}>KPI Framework</div>
          <h2 style={{
            ...css.display, fontSize: 'clamp(32px, 4vw, 56px)',
            color: C.cream, margin: '0 0 12px', letterSpacing: 0,
          }}>
            5 metrics that determine success.
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(247,235,217,0.5)', marginBottom: 56, maxWidth: 560 }}>
            These are the numbers we track weekly. Every scale or pivot decision flows from them.
          </p>
        </Reveal>

        <div className="csel-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 48 }}>
          <KpiCard platform="TikTok" target="1,000+" metric="Videos using the sound by Week 8" accent={C.coral} />
          <KpiCard platform="Spotify" target="8%+" metric="Save rate → triggers Discover Weekly + Release Radar" accent={C.teal} />
          <KpiCard platform="Instagram" target="2%+" metric="DM shares per view — top Reels ranking signal" accent={C.gold} />
          <KpiCard platform="TikTok" target="65%+" metric="Video completion rate validates hook effectiveness" accent={C.reggaeGreen} />
          <KpiCard platform="Spotify" target="20%+" metric="Week-over-week listener growth from Week 3 onwards" accent="#9B7FE8" />
        </div>

        {/* scale/pivot cards */}
        <div className="csel-scale-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {[
            {
              title: 'SCALE when', accent: C.teal, items: [
                'Sound uses growing faster than creator count (organic join)',
                'Spotify save rate crosses 8% — increase Groover immediately',
                'Spark Ads CTR above 1.5% — double daily budget for 7 days',
                'One country outperforming 3× — redirect Iberogram budget there',
              ]
            },
            {
              title: 'PIVOT when', accent: C.coral, items: [
                'Video completion below 45% — hook clip needs to move earlier',
                'Sound uses flat at W3 despite 60+ videos — switch hooks',
                'Spark Ads CTR below 0.8% — kill, pick next organic post',
                'TikTok growing but Spotify flat — check sound library Spotify URI',
              ]
            }
          ].map((card, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div style={{
                padding: '36px 40px',
                border: `1px solid ${card.accent}30`,
                background: `linear-gradient(135deg, ${card.accent}0A 0%, transparent 100%)`,
              }}>
                <div style={{ ...css.label, color: card.accent, marginBottom: 20 }}>{card.title}</div>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {card.items.map((item, j) => (
                    <li key={j} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <Diamond size={8} color={card.accent} style={{ marginTop: 7, flexShrink: 0 }} />
                      <span style={{ fontSize: 14, color: 'rgba(247,235,217,0.75)', lineHeight: 1.6 }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>


      {/* ── NEXT STEPS ──────────────────────────────────────────────── */}
      <Section id="next" bg="#080F18">
        <div className="csel-next-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 100, alignItems: 'start' }}>
          <Reveal>
            <div style={{ ...css.eyebrow, marginBottom: 20 }}>Immediate Actions</div>
            <h2 style={{
              ...css.display, fontSize: 'clamp(38px, 4.5vw, 64px)',
              color: C.cream, margin: '0 0 28px', letterSpacing: 0, lineHeight: 1.0,
            }}>
              8 steps<br />
              <span style={{ color: C.gold }}>before<br />June 1.</span>
            </h2>
            <p style={{ fontSize: 16, lineHeight: 1.75, color: 'rgba(247,235,217,0.6)', marginBottom: 40, maxWidth: 380 }}>
              A campaign that works only on TikTok is half a campaign.
              A strategy that lives in a deck but not in execution is decoration.
              <strong style={{ color: C.coral, fontStyle: 'normal' }}> Mikel Media bridges that gap.</strong>
            </p>
          </Reveal>

          {/* checklist */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {[
              { n: 1, when: 'Today', cost: 'FREE', task: 'Submit song to Spotify for Artists editorial pitch — free, takes 5 minutes, mandatory first step' },
              { n: 2, when: 'Today', cost: '$80–$162', task: 'Sign up on SoundCampaign and launch first TikTok test campaign with the 15-second hook clip' },
              { n: 3, when: 'This week', cost: '$0', task: 'Boris records and posts the stitch seed video: "¿Y tú, cuándo salió tu sol?" — enables Stitch mechanic' },
              { n: 4, when: 'This week', cost: '$0', task: 'Send Iberogram inquiry email AND Instagram DM simultaneously — LATAM agencies respond faster via DM' },
              { n: 5, when: 'This week', cost: '$150–$250', task: 'Hire 5 Fiverr creators in Colombia and Mexico to test Hook #1 and Hook #2 simultaneously' },
              { n: 6, when: 'This week', cost: '$0', task: 'Identify and confirm the strongest 15-second clip from the song — this is the foundation of every brief' },
              { n: 7, when: 'Week 1', cost: '$0', task: 'Send outreach emails to Playlist Push and Groover with Spotify link and campaign brief' },
              { n: 8, when: 'Week 2', cost: '$3,000 dep.', task: 'Sign Iberogram contract, pay deposit, share the creator brief — they need 7 days to activate creators' },
            ].map((step, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="csel-step-row" style={{
                  gap: 20,
                  padding: '20px 0',
                  borderBottom: i < 7 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                  alignItems: 'start',
                }}>
                  <div style={{
                    width: 32, height: 32,
                    border: `1px solid ${C.coral}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    ...css.display, fontSize: 14, color: C.coral, fontWeight: 700, flexShrink: 0,
                  }}>
                    {step.n}
                  </div>
                  <span className="csel-step-when" style={{ fontSize: 11, color: 'rgba(247,235,217,0.45)', letterSpacing: '0.1em', paddingTop: 6, textTransform: 'uppercase' }}>
                    {step.when}
                  </span>
                  <span className="csel-step-task" style={{ fontSize: 14, color: 'rgba(247,235,217,0.75)', lineHeight: 1.6, paddingTop: 4 }}>
                    {step.task}
                  </span>
                  <span className="csel-step-cost" style={{
                    fontSize: 13, fontWeight: 600, textAlign: 'right', paddingTop: 4,
                    color: step.cost === 'FREE' ? C.teal : C.gold,
                  }}>
                    {step.cost}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>


      {/* ── FINAL CTA ──────────────────────────────────────────────── */}
      <div className="csel-final-cta">
        <div className="csel-final-copy">
          <div style={{ ...css.eyebrow, color: C.coral, marginBottom: 12 }}>Ready to move</div>
          <h2 style={{ ...css.display, fontSize: 'clamp(34px, 4vw, 56px)', color: C.cream, margin: '0 0 12px', lineHeight: 1, letterSpacing: 0 }}>
            Launch the campaign.
          </h2>
          <p style={{ margin: 0, color: 'rgba(247,235,217,0.62)', lineHeight: 1.7, fontSize: 15 }}>
            Open WhatsApp to start execution, or download the full plan for internal review.
          </p>
        </div>

        <div className="csel-cta-row">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="csel-button"
            style={{
              display: 'inline-block',
              padding: '18px 44px',
              background: C.coral,
              color: C.cream,
              fontFamily: "'Poppins', Verdana, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              textDecoration: 'none',
              border: 'none',
              cursor: 'pointer',
              transition: 'background 0.2s, transform 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#D96849'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = C.coral; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Let's Launch This Campaign
          </a>
          <a
            href={PLAN_PDF_URL}
            download={PLAN_PDF_FILENAME}
            onClick={downloadPlan}
            className="csel-button"
            style={{
              display: 'inline-block',
              padding: '18px 34px',
              background: 'transparent',
              color: C.gold,
              fontFamily: "'Poppins', Verdana, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              textDecoration: 'none',
              border: `1px solid rgba(242,120,92,0.55)`,
              cursor: 'pointer',
              transition: 'border-color 0.2s, color 0.2s, transform 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.cream; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(242,120,92,0.55)'; e.currentTarget.style.color = C.gold; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Download Full Plan
          </a>
        </div>
      </div>


      {/* ── FOOTER STRIP ────────────────────────────────────────────── */}
      <div className="csel-footer" style={{
        background: C.navyDeep,
        borderTop: `1px solid rgba(255,255,255,0.07)`,
        padding: '32px 48px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 16,
      }}>
        <img src="/clients/cuando-sale-el-sol/logo-mikel-white.png" alt="Mikel Media" style={{ height: 32, opacity: 0.6 }} />
        <div style={{ ...css.label, color: 'rgba(247,235,217,0.35)', textAlign: 'center' }}>
          Cuando Sale el Sol · Boris Bilbraut y los Árboles · Summer 2026 Campaign
        </div>
        <div style={{ fontSize: 12, color: 'rgba(247,235,217,0.3)' }}>
          Prepared May 2026 · Confidential
        </div>
      </div>

    </div>
  );
}
